import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Search, ArrowRight, LifeBuoy } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import {
  HELP_CATEGORIES,
  HELP_ENTRIES,
  type HelpEntry,
} from '@/lib/help/helpContent';

/**
 * Help Center — `/help`.
 *
 * Documents the app screen by screen (what it does / what data / what for),
 * grouped by category, searchable by keyword, bilingual (EN/ES). Each entry is
 * a collapsible card; contextual `<HelpHint>` links deep-link here via
 * `/help#<entryId>` — the matching card auto-expands and scrolls into view.
 *
 * Content lives in `src/lib/help/helpContent.ts` — keep it in sync with the
 * app (see `.claude/rules/help-sync.md`).
 */
export function HelpPage() {
  const t = useT();
  const lang = usePrefsStore((s) => s.language);
  const location = useLocation();
  useFavoritable(t('help.title'));

  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [highlightId, setHighlightId] = useState<string | null>(null);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Deep-link: when the URL carries `#<entryId>`, expand that card, scroll it
  // into view and flash a highlight ring. Runs on mount and on hash change.
  useEffect(() => {
    const id = location.hash.replace(/^#/, '');
    if (!id || !HELP_ENTRIES.some((e) => e.id === id)) return;
    setExpanded((prev) => new Set(prev).add(id));
    setHighlightId(id);
    const timer = window.setTimeout(() => {
      document
        .getElementById(`help-${id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    const clear = window.setTimeout(() => setHighlightId(null), 2400);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clear);
    };
  }, [location.hash]);

  // Search — case-insensitive match over title + content + keywords (both
  // languages, so ES keywords still find an entry while viewing in EN).
  const results = useMemo<HelpEntry[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return HELP_ENTRIES;
    return HELP_ENTRIES.filter((e) => {
      const haystack = [
        e.title.en,
        e.title.es,
        e.whatItDoes[lang],
        e.whatData[lang],
        e.whatFor[lang],
        ...e.keywords.en,
        ...e.keywords.es,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query, lang]);

  const searching = query.trim().length > 0;

  return (
    <div className="page space-y-6">
      <header>
        <h1 className="flex items-center gap-2 text-3xl font-bold">
          <LifeBuoy className="h-7 w-7 text-[var(--color-accent-ink)]" />
          {t('help.title')}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('help.subtitle')}
        </p>
      </header>

      {/* Search */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('help.searchPlaceholder')}
          className="pl-9"
          aria-label={t('help.searchPlaceholder')}
        />
      </div>

      {/* Category index — hidden while searching */}
      {!searching && (
        <nav className="flex flex-wrap gap-2">
          {HELP_CATEGORIES.map((c) => (
            <a
              key={c.id}
              href={`#cat-${c.id}`}
              className="rounded-full border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-1 text-xs font-semibold text-[var(--color-fg-muted)] transition-colors hover:border-[var(--color-accent-ink)] hover:text-[var(--color-fg-primary)]"
            >
              {c.label[lang]}
            </a>
          ))}
        </nav>
      )}

      {/* Searching → flat result list */}
      {searching ? (
        results.length === 0 ? (
          <p className="rounded-lg border border-dashed border-[var(--color-border-default)] p-8 text-center text-sm text-[var(--color-fg-muted)]">
            {t('help.noResults')}
          </p>
        ) : (
          <section className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-subtle)]">
              {t('help.resultsCount', { count: String(results.length) })}
            </p>
            {results.map((e) => (
              <EntryCard
                key={e.id}
                entry={e}
                lang={lang}
                open={expanded.has(e.id)}
                highlight={highlightId === e.id}
                onToggle={() => toggle(e.id)}
              />
            ))}
          </section>
        )
      ) : (
        // Not searching → grouped by category
        HELP_CATEGORIES.map((cat) => {
          const entries = HELP_ENTRIES.filter((e) => e.category === cat.id);
          if (entries.length === 0) return null;
          return (
            <section key={cat.id} id={`cat-${cat.id}`} className="scroll-mt-20 space-y-3">
              <h2 className="text-xl font-bold">{cat.label[lang]}</h2>
              {entries.map((e) => (
                <EntryCard
                  key={e.id}
                  entry={e}
                  lang={lang}
                  open={expanded.has(e.id)}
                  highlight={highlightId === e.id}
                  onToggle={() => toggle(e.id)}
                />
              ))}
            </section>
          );
        })
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function EntryCard({
  entry,
  lang,
  open,
  highlight,
  onToggle,
}: {
  entry: HelpEntry;
  lang: 'en' | 'es';
  open: boolean;
  highlight: boolean;
  onToggle: () => void;
}) {
  const t = useT();
  return (
    <Card
      id={`help-${entry.id}`}
      className={cn(
        'scroll-mt-20 p-0 transition-shadow',
        highlight && 'ring-2 ring-[var(--color-accent)]',
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <span className="font-bold text-[var(--color-fg-primary)]">
          {entry.title[lang]}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-[var(--color-fg-subtle)] transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div className="space-y-4 border-t border-[var(--color-border-subtle)] px-5 py-4">
          <HelpBlock label={t('help.whatItDoes')} text={entry.whatItDoes[lang]} />
          <HelpBlock label={t('help.whatData')} text={entry.whatData[lang]} />
          <HelpBlock label={t('help.whatFor')} text={entry.whatFor[lang]} />

          {entry.keywords[lang].length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {entry.keywords[lang].map((k) => (
                <Badge key={k} tone="neutral" className="normal-case">
                  {k}
                </Badge>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {entry.route && (
              <Link
                to={entry.route}
                className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-accent-ink)] hover:underline"
              >
                {t('help.goToPage')}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            )}
            {entry.related.length > 0 && (
              <span className="flex flex-wrap items-center gap-1.5 text-xs text-[var(--color-fg-muted)]">
                <span className="font-semibold uppercase tracking-wide">
                  {t('help.related')}:
                </span>
                {entry.related.map((rid) => {
                  const r = HELP_ENTRIES.find((e) => e.id === rid);
                  if (!r) return null;
                  return (
                    <a
                      key={rid}
                      href={`#${rid}`}
                      className="text-[var(--color-accent-ink)] hover:underline"
                    >
                      {r.title[lang]}
                    </a>
                  );
                })}
              </span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function HelpBlock({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <p className="mb-0.5 text-xs font-bold uppercase tracking-wide text-[var(--color-fg-subtle)]">
        {label}
      </p>
      <p className="text-sm text-[var(--color-fg-primary)]">{text}</p>
    </div>
  );
}
